import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type ChronicConditionCode = 'DIABETES' | 'HYPERTENSION';
export type PatientMeasureTier = 'CONFIRMED' | 'SUSPECTED';
export type PatientMeasureStatus = 'TESTED_CONTROLLED' | 'TESTED_UNCONTROLLED' | 'NOT_TESTED';

export interface PatientMeasureRow {
  patientId: string;
  patientName: string;
  tier: PatientMeasureTier;
  lastResultDate?: string;
  lastResultSummary?: string;
  status: PatientMeasureStatus;
}

export interface ConditionMeasure {
  condition: ChronicConditionCode;
  displayName: string;
  confirmedCohortSize: number;
  suspectedCohortSize: number;
  testedCount: number;
  testingRatePct: number;
  controlledCount: number;
  controlRatePct: number;
  measureWindowDays: number;
  clinicalTarget: string;
  /** Stable code for clinicalTarget (HBA1C_BELOW, BP_BELOW) with its values; absent on older servers. */
  clinicalTargetKey?: string;
  clinicalTargetParams?: Record<string, string | number>;
  patients: PatientMeasureRow[];
}

export interface QualityMeasureResponse {
  clinicId: string;
  measures: ConditionMeasure[];
}

export const chronicDiseaseModule = {
  /** Two-tier (CONFIRMED/SUSPECTED) chronic-disease registries + testing/control quality
   *  measures — diabetes (HbA1c) and hypertension (BP), clinic-wide. Never blocks/writes
   *  anything, read-only analytics. */
  getQualityMeasures: () =>
    fetchApi<QualityMeasureResponse>('/api/analytics/quality-measures'),

  /** The measures without the patient lists (no names decrypted) — the screen pages the rows (medopd-docs 117). */
  getQualityMeasureSummary: () =>
    fetchApi<QualityMeasureResponse>('/api/analytics/quality-measures?includePatients=false'),

  /** One page of a condition's patient rows, optionally one tier, needing attention first. */
  getConditionPatients: (condition: ChronicConditionCode, page: number, size: number, tier?: PatientMeasureTier) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (tier) params.set('tier', tier);
    return fetchApi<PagedResponse<PatientMeasureRow>>(`/api/analytics/quality-measures/${condition}/patients?${params.toString()}`);
  },
};
